import { useEffect } from 'react';
import { Card, CardContent, Typography, Box, Link, Divider, List, ListItem, ListItemText } from '@mui/material';
import { usePurchaseData } from '../apis/getPurchaseData';
import { useSub } from '../hooks/useSub';
import { useAuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

// purchase-data?sub=11fc4cc6-bad5-4069-9702-697d8e8162d5
export default function PurchaseData() {
  const { sub } = useSub();
  const { login, logout, isAuthenticated, user } = useAuthContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      // Wrap the login call in a setTimeout to ensure MSAL is initialized
      const timer = setTimeout(() => {
        login().catch(error => {
          console.error('Login failed:', error);
          console.log(JSON.stringify(error, null, 2));
          if (error.errorCode === 'user_cancelled') {
            navigate('/');
          }
        });
      }, 100);
        
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, login]);

  const { data, isLoading, error } = usePurchaseData(sub, user?.bearer);
  
  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading data</Typography>;
  if (!data?.data) return <Typography>No data available</Typography>;

  const { formData, ewayData, alfrescoRootName, alfrescoRootId, picaUser, sessionStartTimeUTC, downloadLog, hours } = data?.data;
  const signedUrl = data?.signedUrl;

  const formatLocalTime = (utcTime) => {
    if (!utcTime) return 'N/A';
    const date = new Date(utcTime);
    return date.toLocaleString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Purchase Details
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Id
          </Typography>
          <Typography variant="body1" gutterBottom>
            {sub}
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Property Information
          </Typography>
          <Typography variant="body1" gutterBottom>
            {alfrescoRootName}
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Alfresco Root ID:
          </Typography>
          <Typography variant="body1" gutterBottom>
            {alfrescoRootId}
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Hours:
          </Typography>
          <Typography variant="body1" gutterBottom>
            {hours}
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Session Start Time
          </Typography>
          <Typography variant="body1" gutterBottom>
            {formatLocalTime(sessionStartTimeUTC)}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Customer Information
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Name" 
                secondary={`${formData.firstName} ${formData.lastName}`} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Email" 
                secondary={formData.email} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Address" 
                secondary={`${formData.address}, ${formData.state}`} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="DP Number" 
                secondary={formData.dpNumber} 
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Payment Information
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Amount" 
                secondary={ewayData?.totalAmountDollars || 'N/A'} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Transaction ID" 
                secondary={ewayData?.transactionID || 'N/A'} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Authorization Code" 
                secondary={ewayData?.authorisationCode || 'N/A'} 
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Pica User information
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Pica User ID" 
                secondary={picaUser?.sub || 'N/A'} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Pica User Name" 
                secondary={picaUser?.preferred_username || 'N/A'} 
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            LOA Document
          </Typography>
          <Link 
            href={signedUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ 
              textDecoration: 'underline',
              color: 'primary.main',
              '&:hover': {
                color: 'primary.dark'
              }
            }}
          >
            {signedUrl ? "Download File" : "No download available"}
          </Link>
          
          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Downloads
          </Typography>
          {downloadLog?.length > 0 ? (
            <List>
              {downloadLog.map((log, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={log.filename}
                    secondary={
                      <>
                        <Typography variant="body2" component="span">
                          Document ID: {log.documentId}
                        </Typography>
                        <br />
                        <Typography variant="body2" component="span">
                          Downloaded: {formatLocalTime(log.downloadDateTimeUTC)}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No downloads recorded
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}